// frontend/src/components/InventoryStockList.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Edit,
  Trash2,
  Building,
  User,
  Package,
  Warehouse,
  Download,
  Filter,
  Search,
  Hash,
  DollarSign,
  RefreshCw,
  CheckCircle,
  Eye,
  TrendingDown,
  AlertCircle,
  BarChart3,
  Layers
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./InventoryStockList.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ Helper to extract currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const InventoryStockList = () => {
  const { company } = useAuth();
  const { formatCurrency: contextFormatCurrency } = useCurrency();
  const navigate = useNavigate();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Currency formatter that uses company's currency
  const formatCurrency = useCallback((amount) => {
    const currencyCode = companyCurrency;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [companyCurrency]);

  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [userCompany, setUserCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({
    product_id: "",
    warehouse_id: "",
    search: "",
    status: "active",
    low_stock: false
  });
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // ✅ Product reorder level lookup (productId -> reorder level)
  //    This is used as the effective "min qty" when the stock record
  //    doesn't have its own `min_qty`.
  const [productReorderMap, setProductReorderMap] = useState({});

  // ✅ Effective min qty for a stock row =
  //    stock.min_qty  OR  product.reorder_level  OR  0
  const computeEffectiveMin = useCallback((item) => {
    const stockMin = Number(item?.min_qty || 0);
    if (stockMin > 0) return stockMin;

    const productId = String(
      item?.product_id?._id || item?.product_id || ''
    );
    return Number(productReorderMap[productId] || 0);
  }, [productReorderMap]);

  // ✅ Low stock = available strictly LESS than effective min
  const isLowStockItem = useCallback((item) => {
    const qty = Number(
      item?.available_qty ?? item?.quantity ?? 0
    );
    const min = computeEffectiveMin(item);
    return min > 0 && qty < min;
  }, [computeEffectiveMin]);

  // Computed totals from actual stock data
  const totalStocksCount = stocks.length;
  const totalQuantity = stocks.reduce((sum, s) => sum + (s.quantity || 0), 0);
  const totalValue = stocks.reduce((sum, s) => {
    const subtotal = (s.quantity || 0) * (s.avg_cost || 0);
    const shippingOther = (s.shipping_cost || 0) + (s.other_charges || 0);
    return sum + subtotal + shippingOther;
  }, 0);
  const uniqueProducts = new Set(stocks.map(s => s.product_id?._id).filter(Boolean)).size;
  const uniqueWarehouses = new Set(stocks.map(s => s.warehouse_id?._id).filter(Boolean)).size;

  // ✅ Low stock count uses per-product reorder level (falls back to stock.min_qty)
  const lowStockCount = stocks.filter(s => isLowStockItem(s)).length;

  const fetchStocks = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${url}/api/inventorystock`, {
        headers: getAuthHeaders(),
        params: {
          populate: 'product_id,warehouse_id,company_id',
          status: filters.status,
          product_id: filters.product_id || undefined,
          warehouse_id: filters.warehouse_id || undefined,
        },
        timeout: 10000
      });
      if (!res.data.success) throw new Error(res.data.message || "API error");
      const data = res.data.data || [];
      const sortedData = data.sort((a, b) => (a.stock_id || 0) - (b.stock_id || 0));
      setStocks(sortedData);
      setFilteredData(sortedData);
      if (sortedData[0]?.company_id) setUserCompany(sortedData[0].company_id);
      else if (company) setUserCompany(company);
    } catch (err) {
      setError(err.message);
      setStocks([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [productsRes, warehousesRes] = await Promise.all([
        axios.get(`${url}/api/products/dropdown`, { headers: getAuthHeaders() }),
        axios.get(`${url}/api/warehouse/dropdown`, { headers: getAuthHeaders() })
      ]);
      if (productsRes.data.success) setProducts(productsRes.data.data || []);
      if (warehousesRes.data.success) setWarehouses(warehousesRes.data.data || []);
    } catch (err) {
      // silent
    }
  };

  // ✅ Fetch full products list to build the reorder level map.
  //    The dropdown endpoint often strips extra fields like reorder_level,
  //    so we hit /api/products directly.
  const fetchProductReorderMap = async () => {
    try {
      const res = await axios.get(`${url}/api/products`, {
        headers: getAuthHeaders(),
        params: { status: 'active', limit: 5000 },
        timeout: 10000,
      });

      const list =
        res.data?.data ||
        res.data?.items ||
        res.data?.results ||
        [];

      const map = {};
      list.forEach((p) => {
        const id = String(p._id || p.id || '');
        if (!id) return;

        const reorder = Number(
          p.reorder_level ??
            p.reorder_qty ??
            p.reorder_point ??
            p.min_qty ??
            p.min_stock_level ??
            p.minimum_stock ??
            0
        );
        map[id] = reorder;
      });

      setProductReorderMap(map);
    } catch (err) {
      // silent — fallback to stock.min_qty only
      setProductReorderMap({});
    }
  };

  useEffect(() => {
    fetchStocks();
    fetchDropdowns();
    fetchProductReorderMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, stocks, productReorderMap]);

  const filterData = () => {
    let filtered = [...stocks];
    if (filters.product_id) {
      filtered = filtered.filter(item => (item.product_id?._id || item.product_id) === filters.product_id);
    }
    if (filters.warehouse_id) {
      filtered = filtered.filter(item => (item.warehouse_id?._id || item.warehouse_id) === filters.warehouse_id);
    }
    if (filters.search) {
      const lower = filters.search.toLowerCase();
      filtered = filtered.filter(item => {
        const productName = item.product_id?.product_name || '';
        const productCode = item.product_id?.product_code || '';
        const warehouseName = item.warehouse_id?.warehouse_name || '';
        const warehouseCode = item.warehouse_id?.warehouse_code || '';
        const stockId = item.stock_id?.toString() || '';
        return [productName, productCode, warehouseName, warehouseCode, stockId].some(f => f.toLowerCase().includes(lower));
      });
    }
    if (filters.status) filtered = filtered.filter(item => item.status === filters.status);
    if (filters.low_stock) filtered = filtered.filter(item => isLowStockItem(item));
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleDelete = async (id, productName) => {
    if (!window.confirm(`Delete stock for "${productName}"?`)) return;
    try {
      await axios.delete(`${url}/api/inventorystock/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Deleted");
      fetchStocks();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const clearFilters = () => {
    setFilters({ product_id: "", warehouse_id: "", search: "", status: "active", low_stock: false });
    setShowFilters(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchStocks(), fetchProductReorderMap()]);
    setRefreshing(false);
    toast.success("Refreshed");
  };

  const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num || 0);

  const handleExport = () => {
    if (filteredData.length === 0) { toast.warning("No data"); return; }
    const headers = ["Stock ID","Product Code","Product Name","Warehouse Code","Warehouse Name","Total Qty","Reserved Qty","Available Qty","Avg Cost","Stock Value","Status"];
    const rows = filteredData.map(item => [
      item.stock_id || '',
      item.product_id?.product_code || '',
      item.product_id?.product_name || '',
      item.warehouse_id?.warehouse_code || '',
      item.warehouse_id?.warehouse_name || '',
      item.quantity || 0,
      item.reserved_qty || 0,
      item.available_qty || 0,
      item.avg_cost || 0,
      (item.quantity||0)*(item.avg_cost||0),
      item.status || 'active'
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `inventory-stocks-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Export started");
  };

  const getProductInfo = (item) => ({
    name: item.product_id?.product_name || 'Unknown',
    code: item.product_id?.product_code || 'N/A',
    barcode: item.product_id?.barcode || ''
  });
  const getWarehouseInfo = (item) => ({
    name: item.warehouse_id?.warehouse_name || 'Unknown',
    code: item.warehouse_id?.warehouse_code || 'N/A',
    location: item.warehouse_id?.location || ''
  });
  const calculateStockValue = (item) => (item.quantity || 0) * (item.avg_cost || 0);
  const isLowStock = (item) => isLowStockItem(item);

  const handleViewDetails = (item) => {
    const prod = getProductInfo(item);
    const wh = getWarehouseInfo(item);
    const min = computeEffectiveMin(item);
    alert(`Stock ID: ${item.stock_id}\nProduct: ${prod.name} (${prod.code})\nWarehouse: ${wh.name} (${wh.code})\nTotal Qty: ${formatNumber(item.quantity)}\nReserved: ${formatNumber(item.reserved_qty)}\nAvailable: ${formatNumber(item.available_qty)}\nMin Qty: ${min}\nAvg Cost: ${formatCurrency(item.avg_cost)}\nTotal Value: ${formatCurrency(calculateStockValue(item))}\nStatus: ${item.status}`);
  };

  const paginatedData = filteredData.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  if (loading && stocks.length === 0) return (
    <div className="loading-container"><div className="loading-spinner"></div><div>Loading...</div></div>
  );

  return (
    <div className="inventory-container">
      <div className="header">
        <div>
          <h2>Inventory Stock List</h2>
          {userCompany && (
            <p className="company-info"><Building size={14} /> {typeof userCompany === 'object' ? `${userCompany.company_name} (${userCompany.company_code})` : userCompany}</p>
          )}
        </div>
        <div className="header-actions">
          {error && <div className="error-badge"><AlertCircle size={16}/><span>Error</span></div>}
          <button onClick={handleRefresh} className="refresh-button" disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? "spinning" : ""} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <div className="white-text">
            <span className="stat-value">{totalStocksCount}</span>
            <span className="stat-label">Total Stocks</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="white-text">
            <span className="stat-value">{totalQuantity}</span>
            <span className="stat-label">Total Quantity</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="white-text">
            <span className="stat-value">{formatCurrency(totalValue)}</span>
            <span className="stat-label">Total Value</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="white-text">
            <span className="stat-value">{uniqueProducts}</span>
            <span className="stat-label">Total Products</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="white-text">
            <span className="stat-value">{uniqueWarehouses}</span>
            <span className="stat-label">Total Warehouses</span>
          </div>
        </div>
        <div className="stat-item warning">
          <TrendingDown size={18}/>
          <div>
            <span className="stat-value">{formatNumber(lowStockCount)}</span>
            <span className="stat-label">Low Stock (below Reorder)</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`filters-container ${showFilters ? 'expanded' : ''}`}>
        <div className="filter-header" onClick={() => setShowFilters(!showFilters)}>
          <Filter size={16} /><h4>Filters</h4><span className="filter-toggle">{showFilters ? '▼' : '►'}</span>
        </div>
        {showFilters && (
          <div className="filter-content">
            <div className="filter-fields">
              <div className="filter-field"><Search size={16}/><input type="text" name="search" value={filters.search} onChange={handleFilterChange} placeholder="Search..." className="search-input"/></div>
              <select name="product_id" value={filters.product_id} onChange={handleFilterChange} className="filter-select"><option value="">All Products</option>{products.map(p=><option key={p._id} value={p._id}>{p.product_code} - {p.product_name}</option>)}</select>
              <select name="warehouse_id" value={filters.warehouse_id} onChange={handleFilterChange} className="filter-select"><option value="">All Warehouses</option>{warehouses.map(w=><option key={w._id} value={w._id}>{w.warehouse_code} - {w.warehouse_name}</option>)}</select>
              <select name="status" value={filters.status} onChange={handleFilterChange} className="filter-select"><option value="active">Active Only</option><option value="inactive">Inactive Only</option><option value="">All Status</option></select>
              <div className="checkbox-filter">
                <label>
                  <input type="checkbox" name="low_stock" checked={filters.low_stock} onChange={handleFilterChange}/>
                  <span>Low Stock (below Reorder level)</span>
                </label>
              </div>
              <button onClick={clearFilters} className="clear-filters-btn">Clear Filters</button>
            </div>
          </div>
        )}
      </div>

      <div className="actions-bar">
        <div className="results-count">Showing {paginatedData.length} of {filteredData.length} records</div>
        <div className="action-buttons"><button onClick={handleExport} className="export-button" disabled={filteredData.length===0}><Download size={16}/> Export CSV</button></div>
      </div>

      {error && <div className="error-container"><AlertCircle size={20}/><div className="error-message"><strong>Error:</strong><pre>{error}</pre></div><button onClick={handleRefresh} className="retry-btn">Retry</button></div>}

      <div className="table-container">
        <table className="inventory-table">
          <thead>
            <tr>
              <th>Stock ID</th>
              <th>Product</th>
              <th>Warehouse</th>
              <th>Quantities</th>
              <th>Cost & Value</th>
              <th>Status</th>
              {/* <th style={{ textAlign: "center" }}>Actions</th> */}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {stocks.length === 0
                    ? error
                      ? "Failed to load inventory stocks. Please check your connection and try again."
                      : "No inventory stocks found. Add products and opening balances first."
                    : "No matching records found. Try changing your filters."}
                </td>
              </tr>
            ) : (
              paginatedData.map((item) => {
                const prod = getProductInfo(item);
                const wh = getWarehouseInfo(item);
                const low = isLowStock(item);
                const stockVal = calculateStockValue(item);
                const effectiveMin = computeEffectiveMin(item);

                return (
                  <tr key={item._id} className={low ? 'low-stock-row' : ''}>
                    <td>
                      <div className="stock-id-cell">
                        <div className="ref-id-badge"><Hash size={12}/><strong>{item.stock_id || 'N/A'}</strong></div>
                        <small className="ref-id-sub">ID: {item._id?.slice(-6)}</small>
                      </div>
                    </td>
                    <td>
                      <div className="product-info"><Package size={14}/><div><strong>{prod.name}</strong><small>Code: {prod.code}</small></div></div>
                    </td>
                    <td>
                      <div className="warehouse-info"><Warehouse size={14}/><div><strong>{wh.name}</strong><small>{wh.code} {wh.location && `| ${wh.location}`}</small></div></div>
                    </td>
                    <td>
                      <div className="quantity-cell">
                        <div>Total: <strong>{formatNumber(item.quantity)}</strong></div>
                        <div>Reserved: {formatNumber(item.reserved_qty)}</div>
                        <div>Available: <span className={low ? 'warning' : ''}>{formatNumber(item.available_qty)}</span></div>
                        {effectiveMin > 0 && (
                          <div style={{ fontSize: '11px', color: '#999', marginTop: 2 }}>
                            Min Qty: {formatNumber(effectiveMin)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>Avg Cost: <strong>{formatCurrency(item.avg_cost)}</strong></div>
                      <div>Value: <strong>{formatCurrency(stockVal)}</strong></div>
                    </td>
                    <td>
                      <div><span className={`status-badge ${item.status}`}>{item.status === 'active' ? 'Active' : 'Inactive'}</span></div>
                      {low && <div className="low-stock-alert"><TrendingDown size={12}/> Low Stock</div>}
                    </td>
                    {/* <td className="actions">
                      <button onClick={() => handleViewDetails(item)} className="view-btn" title="View"><Eye size={14}/></button>
                      <button onClick={() => navigate(`/inventorystock/edit/${item._id}`)} className="edit-btn" title="Edit" disabled={item.status === 'inactive'}><Edit size={14}/></button>
                      <button onClick={() => handleDelete(item._id, prod.name)} className="delete-btn" title="Delete"><Trash2 size={14}/></button>
                    </td> */}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="pagination-btn">Previous</button>
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .map((p, i, arr) => {
                const showEllipsis = i > 0 && p - arr[i - 1] > 1;
                return (
                  <React.Fragment key={p}>
                    {showEllipsis && <span className="ellipsis">...</span>}
                    <button onClick={() => setCurrentPage(p)} className={`page-btn ${currentPage === p ? 'active' : ''}`}>{p}</button>
                  </React.Fragment>
                );
              })}
          </div>
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="pagination-btn">Next</button>
        </div>
      )}
    </div>
  );
};

export default InventoryStockList;
